/* eslint-disable @typescript-eslint/naming-convention */

// This code is losely based on
// https://github.com/john-doherty/mixpanel-lite/blob/master/src/mixpanel-lite.js

import cookie from './cookie';
import { getBrowser, getBrowserVersion, getNewUUID, getOS, getReferringDomain } from './utils';

const lib = 'mixpanel-skilllab';
const libVersion = '0.0.3';

type Properties = {
  token?: string;
  $os?: string;
  $browser?: string;
  $browser_version?: number | null;
  $screen_height?: number;
  $screen_width?: number;
  $referrer?: string;
  $referring_domain?: string;
  distinct_id?: string;
  $device_id?: string;
  $user_id?: number;
  mp_lib?: string;
  $lib_version?: string;
  $current_url?: string;
  $anon_distinct_id?: string;
};

const flowTimeMap: Record<string, number> = {};
let debug = false;
let initialized = false;
let trackAnonymous = true;
let disabledReason: 'cookie' | 'test' | null = null;
let properties: Properties = {};
let api_host = 'https://api-eu.mixpanel.com';

const getOrSetPersistentData = (token: string) => {
  const cookieData = cookie.get(token);

  if (!cookieData) {
    const uuid = getNewUUID();
    return cookie.set({ token, id: uuid, deviceId: uuid });
  }

  /* istanbul ignore next */
  return cookieData;
};

type ConfigOptions = {
  debug?: boolean;
  trackAnonymous?: boolean;
  cookieAppName?: string;
  api_host?: string;
};
const init = (token: string, config?: ConfigOptions) => {
  if (config?.api_host) {
    ({ api_host } = config);
  }
  if (config?.debug) {
    debug = true;
  }
  if (config?.trackAnonymous !== undefined) {
    ({ trackAnonymous } = config);
  }
  if (config?.cookieAppName) {
    cookie.setCookieAppName(config.cookieAppName);
  }

  const persistentData = getOrSetPersistentData(token);

  properties = {
    token,
    $os: getOS(),
    $browser: getBrowser(),
    $browser_version: getBrowserVersion(),
    $screen_height: window.outerHeight,
    $screen_width: window.outerWidth,
    $referrer: document.referrer,
    $referring_domain: getReferringDomain(),
    distinct_id: persistentData.distinct_id,
    $user_id: persistentData.$user_id,
    $device_id: persistentData.$device_id,
    mp_lib: lib,
    $lib_version: libVersion,
    $current_url: '',
  };

  if (debug) {
    // eslint-disable-next-line no-console
    console.log(`Mixpanel: ${token}`);
  }
  initialized = true;
};

const disable = (reason: NonNullable<typeof disabledReason>) => {
  if (disabledReason === null) {
    disabledReason = reason;
  }
};

// re-enables mixpanel, if the reason for disabling matches
const enable = (reason: typeof disabledReason) => {
  if (disabledReason === reason) {
    disabledReason = null;
  }
};

// used for better Sentry reporting
class MixpanelError extends Error {}

const send = async ({ type, data }: { type: 'event' | 'set'; data: Record<any, any> }) => {
  const url =
    type === 'set'
      ? `${api_host}/engage?ip=1&verbose=1&data=`
      : `${api_host}/track/?ip=1&verbose=1&data=`;

  if (debug) {
    if (type === 'event') {
      // eslint-disable-next-line no-console
      console.log('[Mixpanel] sending event ', data.event, data.properties);
    } else {
      // eslint-disable-next-line no-console
      console.log('[Mixpanel] setting', data.$set);
    }
  }

  const userId = data.$user_id || data.properties?.$user_id;
  if (!initialized || disabledReason !== null || (!trackAnonymous && !userId)) {
    return;
  }

  try {
    const base64Data = btoa(unescape(encodeURIComponent(JSON.stringify(data))));
    await fetch(`${url}${encodeURIComponent(base64Data)}&_=${new Date().getTime()}`);
  } catch (ex) /* istanbul ignore next */ {
    if (ex instanceof Error) {
      const err = new MixpanelError('Failed to send event to mixpanel');
      // we hack this property on like this, so we don't need an additional babel tranformer for
      // class properties, just for this one case
      (err as any).cause = ex;
    }
  }
};

const setPeople = (data: Record<string, any>) => {
  const dataToSend = {
    $set: {
      $os: properties.$os,
      $browser: properties.$browser,
      $browser_version: properties.$browser_version,
      $initial_referrer: '$direct',
      $initial_referring_domain: '$direct',
      ...data,
    },
    $token: properties.token,
    $distinct_id: properties.distinct_id,
    $device_id: properties.$device_id,
    $user_id: properties.$user_id,
  };
  send({ type: 'set', data: dataToSend });
};

const track = (event: string, data: Record<string, any>) => {
  const dataToSend: Record<any, any> = {
    event,
    properties: {
      ...properties,
      ...data,
      time: Math.round(new Date().getTime() / 1000),
      $current_url: window.location.href,
      $insert_id:
        Math.random().toString(36).substring(2, 10) + Math.random().toString(36).substring(2, 10),
    },
  };

  const flowStartTime = flowTimeMap[event];
  if (flowStartTime) {
    dataToSend.properties.$duration = (new Date().getTime() - flowStartTime) / 1000;
    delete flowTimeMap[event];
  }

  send({ type: 'event', data: dataToSend });
};

const identify = (id: number) => {
  if (properties.$user_id === id) {
    return;
  }
  const distinctId = `${id}`;
  const prevDistinctId = properties.distinct_id;
  properties = {
    ...properties,
    $device_id: prevDistinctId,
    distinct_id: distinctId,
    $user_id: id,
  };

  const dataToSend = {
    ...properties,
    $anon_distinct_id: prevDistinctId,
  };

  cookie.set({ token: properties.token!, id: distinctId, userId: id });
  track('$identify', dataToSend);
};

const time_event = (event: string) => {
  flowTimeMap[event] = new Date().getTime();
};

export default { init, track, identify, disable, enable, people: { set: setPeople }, time_event };
